import { useState, useEffect, useCallback, useMemo } from "react";
import { useAppContext } from "useAppContext";
import { useLoggedInAs } from "shared/hooks";
import { EnumOwnerType } from "gql/generated";
import { isSandboxBannerDismissed } from "../SandboxBanner";

export const useSandbox = () => {
  const { organizations } = useAppContext();
  const loggedInAs = useLoggedInAs();

  // Get the current organization if we're logged into one
  const currentOrganization = useMemo(
    () =>
      loggedInAs?.type === EnumOwnerType.Organization
        ? organizations.find(org => org._id === loggedInAs.entityId)
        : null,
    [loggedInAs, organizations],
  );

  const isSandboxOrganization = currentOrganization?.metadata?.sandbox ?? false;
  const organizationId = currentOrganization?._id ?? "";

  // Compute whether the banner should be shown
  // This is derived state, not async state, so we can compute it directly
  const shouldShowBanner = useMemo(() => {
    if (!isSandboxOrganization || !organizationId) {
      return false;
    }
    return !isSandboxBannerDismissed(organizationId);
  }, [isSandboxOrganization, organizationId]);

  // Track dismissed state locally for immediate UI feedback
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissed state when organization changes
  useEffect(() => {
    setIsDismissed(false);
  }, [organizationId]);

  const handleClose = useCallback(() => {
    setIsDismissed(true);
  }, []);

  return {
    showSandboxBanner: shouldShowBanner && !isDismissed,
    organizationId,
    isSandboxOrganization,
    handleClose,
  };
};
